import {
  NOTIFICATION_EMAIL_CATEGORIES,
  NOTIFICATION_EMAIL_LABELS,
  type NotificationEmailCategory,
} from "@cva/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowLeft, Bot, Unplug } from "lucide-react";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { EnvBadge, Logo } from "../components/env-badge";
import { LegalLinks } from "../components/legal-links";
import { TwoFactorCard } from "../components/two-factor-card";
import { Button } from "../components/ui/button";
import { Card, CardHeader, Skeleton } from "../components/ui/card";
import { Field, Input } from "../components/ui/input";
import { client, orpc } from "../lib/api";
import { authClient } from "../lib/auth-client";
import { lastOrg, signOut, useSession } from "../lib/session";
import { errorMessage, formatDate } from "../lib/utils";

function ConnectedApps() {
  const queryClient = useQueryClient();
  const consents = useQuery({
    queryKey: ["oauth-consents"],
    queryFn: async () => {
      const { data, error } = await authClient.oauth2.getConsents();
      if (error) throw error;
      return data ?? [];
    },
  });
  const clients = useQuery({
    queryKey: ["oauth-consent-clients", consents.data?.map((consent) => consent.clientId)],
    enabled: Boolean(consents.data?.length),
    queryFn: async () => {
      const entries = await Promise.all(
        (consents.data ?? []).map(async (consent) => {
          const { data } = await authClient.oauth2.publicClient({
            query: { client_id: consent.clientId },
          });
          return [consent.clientId, data?.client_name ?? consent.clientId] as const;
        }),
      );
      return new Map(entries);
    },
  });

  return (
    <Card>
      <CardHeader
        title="Verbundene Apps"
        description="KI-Assistenten und andere Apps, denen du über OAuth Zugriff gegeben hast (z. B. über den MCP-Server)."
      />
      {consents.isLoading ? (
        <Skeleton className="m-5 h-16" />
      ) : consents.data?.length === 0 ? (
        <p className="px-5 py-6 text-sm text-zinc-500">Noch keine Apps verbunden.</p>
      ) : (
        <ul className="divide-y divide-zinc-100">
          {consents.data?.map((consent) => (
            <li key={consent.id} className="flex items-center gap-3 px-5 py-3">
              <Bot className="size-5 text-brand-600" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">
                  {clients.data?.get(consent.clientId) ?? consent.clientId}
                </p>
                <p className="truncate text-xs text-zinc-500">
                  {(consent.scopes ?? []).join(", ")} · seit{" "}
                  {formatDate(new Date(consent.createdAt))}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-red-600 hover:bg-red-50"
                onClick={async () => {
                  const { error } = await authClient.oauth2.deleteConsent({ id: consent.id });
                  if (error) return toast.error(error.message ?? "Widerrufen fehlgeschlagen");
                  toast.success("Zugriff widerrufen");
                  await queryClient.invalidateQueries({ queryKey: ["oauth-consents"] });
                }}
              >
                <Unplug /> Widerrufen
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/** Which notifications also come by e-mail (docs/15-zusammenarbeit.md). */
function NotificationSettings() {
  const queryClient = useQueryClient();
  const prefs = useQuery(orpc.notifications.preferences.get.queryOptions());
  const update = useMutation({
    mutationFn: (email: Partial<Record<NotificationEmailCategory, boolean>>) =>
      client.notifications.preferences.update({ email }),
    onSuccess: (data) => {
      queryClient.setQueryData(orpc.notifications.preferences.get.queryKey(), data);
      toast.success("Gespeichert");
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  // Opened from the bell or a mail (#benachrichtigungen).
  const card = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (window.location.hash === "#benachrichtigungen" && prefs.data) {
      card.current?.scrollIntoView({ block: "start" });
    }
  }, [prefs.data]);
  return (
    <div id="benachrichtigungen" ref={card} className="scroll-mt-6">
      <Card>
        <CardHeader
          title="Benachrichtigungen"
          description="In der App (Glocke oben links) siehst du immer alles, was dich betrifft. Hier legst du fest, was zusätzlich per E-Mail kommt – mehrere Ereignisse kurz hintereinander kommen gebündelt in einer Mail."
        />
        {prefs.data ? (
          <div className="space-y-4 p-5">
            {prefs.data.emailAvailable ? null : (
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
                Auf diesem Server ist der E-Mail-Versand noch nicht eingerichtet. Deine Auswahl
                gilt, sobald er eingerichtet ist.
              </p>
            )}
            {NOTIFICATION_EMAIL_CATEGORIES.map((category) => (
              <label key={category} className="flex cursor-pointer items-start gap-3 text-sm">
                <input
                  type="checkbox"
                  checked={prefs.data.email[category]}
                  disabled={update.isPending}
                  onChange={(event) => update.mutate({ [category]: event.target.checked })}
                  className="mt-0.5 size-4 shrink-0 accent-brand-600"
                />
                <span>
                  <span className="font-medium text-zinc-900">
                    {NOTIFICATION_EMAIL_LABELS[category].label} per E-Mail
                  </span>
                  <span className="block text-zinc-500">
                    {NOTIFICATION_EMAIL_LABELS[category].description}
                  </span>
                </span>
              </label>
            ))}
            <p className="text-xs text-zinc-500">
              Erledigte Unterhaltungen und Änderungen deiner Mitgliedschaft erscheinen nur in der
              App. Benachrichtigungen werden nach 90 Tagen gelöscht.
            </p>
          </div>
        ) : (
          <Skeleton className="m-5 h-32" />
        )}
      </Card>
    </div>
  );
}

export function AccountPage() {
  const queryClient = useQueryClient();
  const { data: session } = useSession();
  const [name, setName] = useState<string | null>(null);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [busy, setBusy] = useState<"profile" | "password" | null>(null);
  const back = lastOrg();

  async function saveProfile(event: FormEvent) {
    event.preventDefault();
    if (!name?.trim()) return;
    setBusy("profile");
    const { error } = await authClient.updateUser({ name: name.trim() });
    setBusy(null);
    if (error) return toast.error(error.message ?? "Speichern fehlgeschlagen");
    await queryClient.invalidateQueries({ queryKey: ["session"] });
    setName(null);
    toast.success("Gespeichert");
  }

  async function changePassword(event: FormEvent) {
    event.preventDefault();
    setBusy("password");
    const { error } = await authClient.changePassword({
      currentPassword,
      newPassword,
      revokeOtherSessions: true,
    });
    setBusy(null);
    if (error)
      return toast.error(
        error.status === 400
          ? "Das aktuelle Passwort ist falsch."
          : (error.message ?? "Ändern fehlgeschlagen"),
      );
    setCurrentPassword("");
    setNewPassword("");
    toast.success("Passwort geändert. Andere Sitzungen wurden abgemeldet.");
  }

  return (
    <div className="min-h-full">
      <header className="border-b border-zinc-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <Logo />
            <EnvBadge />
          </div>
          {back ? (
            <Link
              to="/o/$orgId"
              params={{ orgId: back }}
              className="flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-900"
            >
              <ArrowLeft className="size-4" /> Zurück
            </Link>
          ) : null}
        </div>
      </header>
      <main className="mx-auto max-w-3xl space-y-6 px-6 py-8">
        <h1 className="text-[28px] leading-[34px] font-bold tracking-tight text-ink">Konto</h1>
        {session ? (
          <>
            <Card>
              <CardHeader title="Profil" />
              <form onSubmit={saveProfile} className="space-y-4 p-5">
                <Field label="Name">
                  <Input
                    value={name ?? session.user.name}
                    onChange={(e) => setName(e.target.value)}
                    className="max-w-md"
                  />
                </Field>
                <Field label="E-Mail">
                  <Input value={session.user.email} disabled className="max-w-md" />
                </Field>
                <Button type="submit" loading={busy === "profile"} disabled={name === null}>
                  Speichern
                </Button>
              </form>
            </Card>
            <Card>
              <CardHeader title="Passwort ändern" />
              <form onSubmit={changePassword} className="space-y-4 p-5">
                <Field label="Aktuelles Passwort">
                  <Input
                    type="password"
                    autoComplete="current-password"
                    required
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="max-w-md"
                  />
                </Field>
                <Field label="Neues Passwort" hint="Mindestens 10 Zeichen">
                  <Input
                    type="password"
                    autoComplete="new-password"
                    minLength={10}
                    required
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="max-w-md"
                  />
                </Field>
                <Button type="submit" loading={busy === "password"}>
                  Passwort ändern
                </Button>
              </form>
            </Card>
            <NotificationSettings />
            <TwoFactorCard />
            <ConnectedApps />
            <Button variant="secondary" onClick={() => void signOut()}>
              Abmelden
            </Button>
          </>
        ) : (
          <Skeleton className="h-64" />
        )}
        <LegalLinks className="pt-6" />
      </main>
    </div>
  );
}
